import { PayloadAction, createSlice } from "@reduxjs/toolkit";

export interface UserInfo {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
}

export interface SignUpState {
    activeStep: number;
    signUpId: string;
    userInfo: UserInfo;
    accountType: "Corporate" | "Individual" | null;
    organizationName: string;
}

function extractStateFromSessionStorage(): SignUpState {
    const stateFromStorage = sessionStorage.getItem('customer-signup-form')

    return stateFromStorage ? JSON.parse(stateFromStorage) : {
        activeStep: 0,
        signUpId: '',
        userInfo: {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
        },
        accountType: null,
        organizationName: '', 
    };
}

export const signUpSlice = createSlice({
    name: 'signUp',
    initialState: extractStateFromSessionStorage(),
    selectors: {
        activeStepSelector: (state) => state.activeStep,
        userInfoSelector: (state) => {
            return {
                ...state.userInfo,
                signUpId: state.signUpId
            };
        },
        accountTypeSelector: (state) => state.accountType,
        organizationNameSelector: (state) => state.organizationName,
        fullSignUpInfoSelector: (state) => ({
            ...state.userInfo,
            signUpId: state.signUpId,
            accountType: state.accountType,
            organizationName: state.organizationName,
        }),
    },
    reducers: {
        setActiveStep: (state, action: PayloadAction<number>) => {
            state.activeStep = action.payload;
            sessionStorage.setItem('customer-signup-form', JSON.stringify(state));
        },
        setUserInfo: (state, action: PayloadAction<UserInfo>) => {
            state.userInfo.firstName = action.payload.firstName;
            state.userInfo.lastName = action.payload.lastName;
            state.userInfo.email = action.payload.email;
            state.userInfo.phone = action.payload.phone;
            sessionStorage.setItem('customer-signup-form', JSON.stringify(state));
        },
        setSignUpId: (state, action: PayloadAction<string>) => {
            state.signUpId = action.payload;
            sessionStorage.setItem('customer-signup-form', JSON.stringify(state));
        },
        // ADDED NEW ACTIONS
        setAccountType: (state, action: PayloadAction<"Corporate" | "Individual">) => {
            state.accountType = action.payload;
            sessionStorage.setItem('customer-signup-form', JSON.stringify(state));
        },
        setOrganizationName: (state, action: PayloadAction<string>) => {
            state.organizationName = action.payload;
            sessionStorage.setItem('customer-signup-form', JSON.stringify(state));
        },
        // COMBINED ACTION FOR SETTING ALL USER INFO AT ONCE
        setCompleteUserInfo: (
            state, 
            action: PayloadAction<{
                firstName: string;
                lastName: string;
                email: string;
                phone: string;
                accountType: "Corporate" | "Individual";
                organizationName?: string;
            }>
        ) => {
            state.userInfo.firstName = action.payload.firstName;
            state.userInfo.lastName = action.payload.lastName;
            state.userInfo.email = action.payload.email;
            state.userInfo.phone = action.payload.phone;
            state.accountType = action.payload.accountType;
            if (action.payload.organizationName) {
                state.organizationName = action.payload.organizationName;
            }
            sessionStorage.setItem('customer-signup-form', JSON.stringify(state));
        },
        clearState: (state) => {
            state.activeStep = 0;
            state.signUpId = '';
            state.userInfo = {
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
            };
            state.accountType = null;
            state.organizationName = '';
            sessionStorage.removeItem('customer-signup-form');
        }
    }
});

export const { 
    setActiveStep,
    setUserInfo, 
    setSignUpId,
    setAccountType,
    setOrganizationName,
    setCompleteUserInfo,
    clearState,
} = signUpSlice.actions;

export const { 
    activeStepSelector,
    userInfoSelector,
    accountTypeSelector,
    organizationNameSelector,
    fullSignUpInfoSelector,
} = signUpSlice.selectors;

export default signUpSlice.reducer;